import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { isAxiosError } from 'axios'
import type { IErrorResponse } from '@/api/interfaces'
import type { FundingRequestListPrivateDto } from '@/api/kubik/credit'
import { useCreateLost } from '@/api/kubik/credit'
import { mapApplyFinancingOfferHandlerOptionsToDto } from '@/components/composed/financing/helpers'
import type { TApplyLostFinancingOfferHandler } from '@/components/composed/financing/types'
import { getLocation } from '@/logic/app/reducer'
import { sendSubmitFormStatusEventWithUserInfo } from '@/logic/metrika/constants'
import { pushGtmEventOnSubmitOrder } from '@/logic/metrika/financing/gtmHelpers'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { getUserRegion } from '@/logic/userRegion'
import type { RootState } from '@/redux/interfaces'
import { type EFinancingProductId } from '@/types'
import type { TUseApplyLostFinancingOfferResult } from './types'

const mapState = (state: RootState) => ({
  userGtmInfo: getUserGtmInfo()(state),
  userRegion: getUserRegion()(state),
  location: getLocation()(state),
})

export const useApplyLostFinancingOffer = (): TUseApplyLostFinancingOfferResult => {
  const { userGtmInfo, userRegion, location } = useSelector(mapState)
  const { mutateAsync: createLostFinancingOffer, isLoading } = useCreateLost()

  const sendSuccessGtmEvents = useCallback(
    (productIds: EFinancingProductId[]) => {
      pushGtmEventOnSubmitOrder(userGtmInfo, userRegion.name, [], productIds)
    },
    [userGtmInfo, userRegion.name],
  )

  const sendErrorGtmEvents = useCallback(
    (firstProductId: EFinancingProductId, errorMessage: string) => {
      sendSubmitFormStatusEventWithUserInfo(firstProductId, `err: ${errorMessage}`, userGtmInfo, location.pathname)
    },
    [location.pathname, userGtmInfo],
  )

  const applyLostFinancingOffer: TApplyLostFinancingOfferHandler = useCallback(
    async options => {
      const dto = mapApplyFinancingOfferHandlerOptionsToDto(options)

      try {
        await createLostFinancingOffer(dto)

        sendSuccessGtmEvents(dto.productIds)
      } catch (error) {
        if (isAxiosError<FundingRequestListPrivateDto & IErrorResponse>(error) && error.response) {
          const errorMessage = error.response.data.message
          const firstProductId = dto.productIds[0]

          if (firstProductId) {
            sendErrorGtmEvents(firstProductId, errorMessage)
          }

          throw new Error(errorMessage)
        }

        throw error
      }
    },
    [createLostFinancingOffer, sendErrorGtmEvents, sendSuccessGtmEvents],
  )

  return {
    applyLostFinancingOffer,
    isLoading,
  }
}
